<script setup>
// AIVIO timers: create / edit form for one timer rule.
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useEventListener } from '@vueuse/core';

import { useAlert } from 'dashboard/composables';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import AivioTimersAPI from 'dashboard/api/aivioTimers';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import TimerActionRow from './TimerActionRow.vue';
import {
  ACTIONS,
  DEFAULT_MAX_MINUTES,
  conversationRef,
  TRIGGERS,
  draftToPayload,
  durationToMinutes,
  extractErrors,
  formatDuration,
  i18nKey,
  isValidMinutes,
  newAction,
  previewPayload,
  ruleToDraft,
  shortUnits,
} from './timerUtils';

const props = defineProps({
  accountId: { type: [Number, String], required: true },
  maxMinutes: { type: Number, default: DEFAULT_MAX_MINUTES },
  maxActions: { type: Number, default: 10 },
  allowedActions: { type: Array, default: () => [] },
  inboxOptions: { type: Array, default: () => [] },
  labelOptions: { type: Array, default: () => [] },
  agentOptions: { type: Array, default: () => [] },
  teamOptions: { type: Array, default: () => [] },
});

const emit = defineEmits(['saved']);

const { t } = useI18n();

const dialogRef = ref(null);
const discardDialogRef = ref(null);
const contentRef = ref(null);
const isOpen = ref(false);
const draft = ref(ruleToDraft(null));
const errors = ref([]);
const isSaving = ref(false);
const preview = ref(null);
const previewErrors = ref([]);
const isPreviewing = ref(false);

let actionKey = 0;
const withKey = action => {
  actionKey += 1;
  return { ...action, key: actionKey };
};

const isEditing = computed(() => !!draft.value.id);

const dialogTitle = computed(() =>
  isEditing.value
    ? t('AIVIO_TIMERS.FORM.EDIT_TITLE')
    : t('AIVIO_TIMERS.FORM.NEW_TITLE')
);

const triggerOptions = computed(() =>
  TRIGGERS.map(value => ({
    value,
    label: t(`AIVIO_TIMERS.TRIGGERS.${i18nKey(value)}.LABEL`),
  }))
);

const triggerExplanation = computed(() =>
  t(`AIVIO_TIMERS.TRIGGERS.${i18nKey(draft.value.trigger)}.EXPLAIN`)
);

const unitOptions = computed(() => [
  { value: 'minutes', label: t('AIVIO_TIMERS.UNITS.MINUTES') },
  { value: 'hours', label: t('AIVIO_TIMERS.UNITS.HOURS') },
  { value: 'days', label: t('AIVIO_TIMERS.UNITS.DAYS') },
]);

const actionTypes = computed(() =>
  ACTIONS.filter(
    name => !props.allowedActions.length || props.allowedActions.includes(name)
  ).map(value => ({
    value,
    label: t(`AIVIO_TIMERS.ACTIONS.${i18nKey(value)}`),
  }))
);

const minutes = computed(() =>
  durationToMinutes(draft.value.durationValue, draft.value.durationUnit)
);

const durationError = computed(() =>
  isValidMinutes(minutes.value, props.maxMinutes)
    ? ''
    : t('AIVIO_TIMERS.FORM.DURATION_ERROR', {
        max: formatDuration(props.maxMinutes, shortUnits(t)),
      })
);

const canAddAction = computed(
  () => draft.value.actions.length < props.maxActions
);

// The preview depends only on the condition: reset it when the condition changes.
watch(
  () => [draft.value.trigger, minutes.value, draft.value.inbox_ids.join(',')],
  () => {
    preview.value = null;
    previewErrors.value = [];
  }
);

// ---- unsaved changes: closing (Escape, click outside, «Отмена») never loses the draft silently.
// Dialog.vue closes itself and then emits 'close'; the draft lives here, so a dirty close asks
// «Закрыть без сохранения?» and re-opens the form with the same draft when the answer is no.
let savedSnapshot = '';
let discardConfirmed = false;
const snapshot = () => JSON.stringify(draftToPayload(draft.value));
const isDirty = () => snapshot() !== savedSnapshot;

const showForm = () => {
  isOpen.value = true;
  dialogRef.value?.open();
};

const open = (rule = null) => {
  const next = ruleToDraft(rule);
  next.actions = next.actions.map(withKey);
  draft.value = next;
  savedSnapshot = snapshot();
  errors.value = [];
  preview.value = null;
  previewErrors.value = [];
  showForm();
};

const onFormClose = () => {
  isOpen.value = false;
  if (!isDirty()) return;
  discardConfirmed = false;
  // Dialog.close() finishes closing the native <dialog> after emitting 'close'.
  nextTick(() => discardDialogRef.value?.open());
};

const confirmDiscard = () => {
  discardConfirmed = true;
  discardDialogRef.value?.close();
};

const onDiscardClose = () => {
  if (!discardConfirmed) nextTick(showForm);
};

const requestClose = () => dialogRef.value?.close();

// ---- Escape with a ComboBox / TagMultiSelectComboBox dropdown open closes only that dropdown.
// The components do not handle Escape themselves, and an uncancelled Escape keydown makes the
// native <dialog> close. Their trigger sits right before the dropdown (role="listbox") wrapper.
const closeOpenDropdown = () => {
  const lists = contentRef.value?.querySelectorAll('[role="listbox"]') || [];
  const list = [...lists].find(el => el.offsetParent !== null);
  if (!list) return false;
  list.parentElement?.parentElement?.firstElementChild?.click();
  return true;
};

useEventListener(
  document,
  'keydown',
  event => {
    if (event.key !== 'Escape' || !isOpen.value || !contentRef.value) return;
    const dialogs = document.querySelectorAll('dialog[open]');
    const topmost = dialogs[dialogs.length - 1];
    if (!topmost?.contains(contentRef.value)) return;
    if (closeOpenDropdown()) {
      event.preventDefault();
      event.stopPropagation();
    }
  },
  { capture: true }
);

const addAction = () => {
  if (!canAddAction.value) return;
  draft.value.actions.push(withKey(newAction('notify')));
};

const removeAction = index => {
  draft.value.actions.splice(index, 1);
};

const updateAction = (index, action) => {
  draft.value.actions.splice(index, 1, action);
};

const runPreview = async () => {
  if (durationError.value) return;
  isPreviewing.value = true;
  previewErrors.value = [];
  try {
    const { data } = await AivioTimersAPI.preview(previewPayload(draft.value));
    preview.value = data.payload;
  } catch (error) {
    preview.value = null;
    previewErrors.value = extractErrors(error);
    if (!previewErrors.value.length) {
      previewErrors.value = [t('AIVIO_TIMERS.PREVIEW.ERROR')];
    }
  } finally {
    isPreviewing.value = false;
  }
};

const previewText = computed(() => {
  if (!preview.value) return '';
  const key = preview.value.capped
    ? 'AIVIO_TIMERS.PREVIEW.RESULT_CAPPED'
    : 'AIVIO_TIMERS.PREVIEW.RESULT';
  return t(key, { n: preview.value.count });
});

const conversationPath = displayId =>
  frontendURL(conversationUrl({ accountId: props.accountId, id: displayId }));

const save = async () => {
  errors.value = [];
  if (durationError.value) errors.value.push(durationError.value);
  if (!draft.value.actions.length) {
    errors.value.push(t('AIVIO_TIMERS.FORM.NO_ACTIONS'));
  }
  if (errors.value.length) return;

  isSaving.value = true;
  const payload = draftToPayload(draft.value);
  try {
    const { data } = isEditing.value
      ? await AivioTimersAPI.update(draft.value.id, payload)
      : await AivioTimersAPI.create(payload);
    useAlert(t('AIVIO_TIMERS.SAVED'));
    emit('saved', data.payload);
    savedSnapshot = snapshot();
    dialogRef.value?.close();
  } catch (error) {
    errors.value = extractErrors(error);
    if (!errors.value.length) errors.value = [t('AIVIO_TIMERS.ERRORS.SAVE')];
  } finally {
    isSaving.value = false;
  }
};

// Enter in a text field (name, duration, combobox search) must not submit the whole form.
const preventImplicitSubmit = event => {
  if (event.target?.tagName === 'INPUT') event.preventDefault();
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    position="top"
    overflow-y-auto
    :title="dialogTitle"
    @confirm="save"
    @close="onFormClose"
  >
    <div
      ref="contentRef"
      class="flex flex-col gap-5"
      @keydown.enter="preventImplicitSubmit"
    >
      <Input
        v-model="draft.name"
        :label="t('AIVIO_TIMERS.FORM.NAME')"
        :placeholder="t('AIVIO_TIMERS.FORM.NAME_PLACEHOLDER')"
        maxlength="80"
      />

      <div class="flex flex-col gap-2">
        <span class="text-heading-3 text-n-slate-12">
          {{ t('AIVIO_TIMERS.FORM.CONDITION') }}
        </span>
        <div class="flex flex-wrap items-center gap-2">
          <Select v-model="draft.trigger" :options="triggerOptions" />
          <Input
            v-model="draft.durationValue"
            type="number"
            class="w-24"
            :message-type="durationError ? 'error' : 'info'"
          />
          <Select v-model="draft.durationUnit" :options="unitOptions" />
        </div>
        <p class="mb-0 text-sm text-n-slate-11">{{ triggerExplanation }}</p>
        <p v-if="durationError" class="mb-0 text-sm text-n-ruby-11">
          {{ durationError }}
        </p>
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-heading-3 text-n-slate-12">
          {{ t('AIVIO_TIMERS.FORM.INBOXES') }}
        </span>
        <TagMultiSelectComboBox
          v-model="draft.inbox_ids"
          :options="inboxOptions"
          :placeholder="t('AIVIO_TIMERS.ALL_INBOXES')"
          :empty-state="t('AIVIO_TIMERS.FORM.NOTHING_FOUND')"
        />
        <p class="mb-0 text-xs text-n-slate-11">
          {{ t('AIVIO_TIMERS.FORM.INBOXES_HINT') }}
        </p>
      </div>

      <div class="flex items-start gap-3">
        <Switch v-model="draft.respect_working_hours" class="mt-1" />
        <div class="flex flex-col gap-0.5">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('AIVIO_TIMERS.FORM.WORKING_HOURS') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('AIVIO_TIMERS.FORM.WORKING_HOURS_HINT') }}
          </span>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-heading-3 text-n-slate-12">
          {{ t('AIVIO_TIMERS.FORM.MODE') }}
        </span>
        <div class="grid gap-3 sm:grid-cols-2">
          <RadioCard
            id="aivio-timer-mode-log"
            :label="t('AIVIO_TIMERS.MODES.LOG')"
            :description="t('AIVIO_TIMERS.MODES.LOG_HINT')"
            :is-active="draft.mode === 'log'"
            @select="draft.mode = 'log'"
          />
          <RadioCard
            id="aivio-timer-mode-live"
            :label="t('AIVIO_TIMERS.MODES.LIVE')"
            :description="t('AIVIO_TIMERS.MODES.LIVE_HINT')"
            :is-active="draft.mode === 'live'"
            @select="draft.mode = 'live'"
          />
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <span class="text-heading-3 text-n-slate-12">
          {{ t('AIVIO_TIMERS.FORM.ACTIONS') }}
        </span>
        <TimerActionRow
          v-for="(action, index) in draft.actions"
          :key="action.key"
          :model-value="action"
          :action-types="actionTypes"
          :label-options="labelOptions"
          :agent-options="agentOptions"
          :team-options="teamOptions"
          @update:model-value="value => updateAction(index, value)"
          @remove="removeAction(index)"
        />
        <p v-if="!draft.actions.length" class="mb-0 text-sm text-n-slate-11">
          {{ t('AIVIO_TIMERS.FORM.NO_ACTIONS') }}
        </p>
        <Button
          type="button"
          icon="i-lucide-plus"
          variant="faded"
          color="slate"
          size="sm"
          class="self-start"
          :label="t('AIVIO_TIMERS.FORM.ADD_ACTION')"
          :disabled="!canAddAction"
          @click="addAction"
        />
      </div>

      <div class="flex items-center gap-3">
        <Switch v-model="draft.enabled" />
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('AIVIO_TIMERS.FORM.ENABLED') }}
        </span>
      </div>

      <div
        class="flex flex-col gap-2 p-3 rounded-xl bg-n-alpha-1 border border-n-weak"
      >
        <div class="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            icon="i-lucide-scan-search"
            color="slate"
            size="sm"
            :label="t('AIVIO_TIMERS.PREVIEW.BUTTON')"
            :is-loading="isPreviewing"
            :disabled="isPreviewing || !!durationError"
            @click="runPreview"
          />
          <span v-if="previewText" class="text-sm text-n-slate-12">
            {{ previewText }}
          </span>
          <span v-else class="text-xs text-n-slate-11">
            {{ t('AIVIO_TIMERS.PREVIEW.HINT') }}
          </span>
        </div>
        <div
          v-if="preview && preview.sample_display_ids?.length"
          class="flex flex-wrap items-center gap-2 text-xs text-n-slate-11"
        >
          <span>{{ t('AIVIO_TIMERS.PREVIEW.SAMPLE') }}</span>
          <a
            v-for="displayId in preview.sample_display_ids"
            :key="displayId"
            :href="conversationPath(displayId)"
            target="_blank"
            rel="noopener noreferrer"
            class="text-n-blue-11 hover:underline"
          >
            {{ conversationRef(displayId) }}
          </a>
        </div>
        <p
          v-for="message in previewErrors"
          :key="message"
          class="mb-0 text-sm text-n-ruby-11"
        >
          {{ message }}
        </p>
      </div>

      <div
        v-if="errors.length"
        class="flex flex-col gap-1 p-3 rounded-xl bg-n-ruby-2 border border-n-ruby-4"
      >
        <p
          v-for="message in errors"
          :key="message"
          class="mb-0 text-sm text-n-ruby-11"
        >
          {{ message }}
        </p>
      </div>
    </div>

    <!-- Default Dialog.vue footer buttons, but sticky: the form scrolls
         inside the dialog element; «Сохранить» stays visible. -->
    <template #footer>
      <div
        class="sticky bottom-0 z-10 flex items-center justify-between gap-3 px-6 pt-4 pb-6 -mx-6 -mb-6 border-t rounded-b-xl border-n-weak bg-n-alpha-3 backdrop-blur-[100px]"
      >
        <Button
          type="button"
          variant="faded"
          color="slate"
          class="w-full"
          :label="t('DIALOG.BUTTONS.CANCEL')"
          @click="requestClose"
        />
        <Button
          type="submit"
          color="blue"
          class="w-full"
          :label="t('AIVIO_TIMERS.FORM.SAVE')"
          :is-loading="isSaving"
          :disabled="isSaving"
        />
      </div>
    </template>
  </Dialog>

  <Dialog
    ref="discardDialogRef"
    type="alert"
    width="md"
    :title="t('AIVIO_TIMERS.FORM.DISCARD.TITLE')"
    :description="t('AIVIO_TIMERS.FORM.DISCARD.DESCRIPTION')"
    :cancel-button-label="t('AIVIO_TIMERS.FORM.DISCARD.KEEP')"
    :confirm-button-label="t('AIVIO_TIMERS.FORM.DISCARD.CONFIRM')"
    @confirm="confirmDiscard"
    @close="onDiscardClose"
  />
</template>
